import SummaryDashboard, {
  BreakdownGroup,
  DateRange,
  SummaryCardConfig,
} from 'components/dashboard/SummaryDashboard';
import { AlertTriangle, CheckCircle, FileText, Search } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const QuickSearch: React.FC<{
  placeholder: string;
  onSearch: (val: string) => void;
}> = ({ placeholder, onSearch }) => {
  const [val, setVal] = useState('');

  return (
    <div className="flex gap-2 flex-1 min-w-60">
      <div className="relative flex-1">
        <Search
          size={13}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && val.trim()) onSearch(val.trim());
          }}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition"
        />
      </div>
      <button
        onClick={() => {
          if (val.trim()) onSearch(val.trim());
        }}
        className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
      >
        Search
      </button>
    </div>
  );
};

const NTSADashboard: React.FC = () => {
  const navigate = useNavigate();

  const goToFines = (smsStatus: string, range: DateRange) =>
    navigate(
      `/fines/1?smsStatus=${smsStatus}&dateCapturedFrom=${range.from}&dateCapturedTo=${range.to}`,
    );

  return (
    <SummaryDashboard
      title="NTSA Offences Dashboard"
      subtitle="Traffic Enforcement - SMS Notifications"
      logo={{ src: '/ntsa-logo.svg', alt: 'NTSA' }}
      showWatermark
      cardColumns="md:grid-cols-3"
      buildCards={(summary, { pct, range }): SummaryCardConfig[] => {
        const totalFines = summary ? summary.smsFailed + summary.smsSent : 0;
        const smsSent = summary?.smsSent ?? 0;
        const smsFailed = summary?.smsFailed ?? 0;

        return [
          {
            label: 'Total Offences',
            value: totalFines,
            icon: <FileText size={22} className="text-gray-600" />,
            iconBg: 'bg-gray-100',
            valueColor: 'text-gray-900',
            borderColor: 'border-gray-200',
          },
          {
            label: 'SMS Sent',
            value: smsSent,
            sublabel: `${pct(smsSent, totalFines)}% of total`,
            icon: <CheckCircle size={22} className="text-emerald-600" />,
            iconBg: 'bg-emerald-50',
            valueColor: 'text-emerald-700',
            borderColor: 'border-emerald-200 hover:border-emerald-400',
            clickable: true,
            onClick: () => goToFines('S', range),
          },
          {
            label: 'SMS Not Sent',
            value: smsFailed,
            sublabel: `${pct(smsFailed, totalFines)}% of total`,
            icon: <AlertTriangle size={22} className="text-amber-600" />,
            iconBg: 'bg-amber-50',
            valueColor: 'text-amber-700',
            borderColor: 'border-amber-200 hover:border-amber-400',
            clickable: true,
            onClick: () => goToFines('F', range),
          },
        ];
      }}
      buildBreakdownGroups={(summary, { pct }): BreakdownGroup[] => {
        const smsSent = summary.smsSent ?? 0;
        const smsFailed = summary.smsFailed ?? 0;
        const totalFines = smsFailed + smsSent;
        const sentPct = pct(smsSent, totalFines);
        const failedPct = pct(smsFailed, totalFines);

        return [
          {
            trackBg: 'bg-gray-100',
            segments: [{ color: 'bg-emerald-500', width: sentPct }],
            items: [
              {
                dot: 'bg-emerald-500',
                label: 'Sent',
                value: smsSent,
                pctVal: sentPct,
              },
              {
                dot: 'bg-red-400',
                label: 'Failed',
                value: smsFailed,
                pctVal: failedPct,
              },
            ],
          },
        ];
      }}
    >
      {({ range }) => (
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
            Quick Search
          </h2>
          <div className="flex gap-3 flex-wrap">
            <QuickSearch
              placeholder="Search by Ticket No."
              onSearch={(val) => navigate(`/fines/1?ticketNumber=${val}`)}
            />
            <QuickSearch
              placeholder="Search by Vehicle Registration"
              onSearch={(val) =>
                navigate(
                  `/fines/1?vehicleRegistrationNumber=${val.toUpperCase()}`,
                )
              }
            />
          </div>
        </div>
      )}
    </SummaryDashboard>
  );
};

export default NTSADashboard;
